import {
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, CircularProgress, Drawer } from "@mui/material";
import { LifecycleManagementAllEvents } from "@lib/ShiOneClient";
import {
  encodeRouteSegment,
  isRouteSafeSegment,
  joinPath,
  toRouteSlug,
  useDiscardGuardedClose,
  useRouteNotFoundToast,
  useRoutedDialog,
  useRoutedTab,
} from "shared-ui/src/hooks/routedOverlays";
import {
  LIFECYCLE_EVENT_DEFAULT_TAB,
  LIFECYCLE_EVENT_PATH,
  LIFECYCLE_SEGMENTS,
  type LifecycleEventTab,
} from "@features/assets-feature/routing/lifecycleRouteSegments";
import { AssetLifecycleManagementDetailDrawer } from "./AssetLifecycleManagementDetailDrawer";
import {
  type AssetLifecycleManagementDrawerTabName,
  AssetLifecycleManagementDrawerTabNames,
  LIFECYCLE_MANAGEMENT_EVENT_DATA_SOURCE,
} from "./constants";
import { useGetLifecycleEventByIdentifier } from "./hooks/useGetLifecycleEventByIdentifier";

const LIFECYCLE_EVENT_NOT_FOUND_MESSAGE =
  "That lifecycle event could not be found.";

const TAB_SLUGS = AssetLifecycleManagementDrawerTabNames.map((name) => ({
  name,
  slug: toRouteSlug(name) as LifecycleEventTab,
}));
const MANUAL_EVENT_HIDDEN_TABS: ReadonlySet<AssetLifecycleManagementDrawerTabName> =
  new Set(["Orders", "Assets"]);

const toTabSlug = (
  name: AssetLifecycleManagementDrawerTabName | undefined,
): LifecycleEventTab =>
  TAB_SLUGS.find((tab) => tab.name === name)?.slug ??
  LIFECYCLE_EVENT_DEFAULT_TAB;

const toTabName = (
  slug: string | undefined,
): AssetLifecycleManagementDrawerTabName | undefined =>
  TAB_SLUGS.find((tab) => tab.slug === slug)?.name;

const OPEN_EVENT_PATH = `${LIFECYCLE_EVENT_PATH}/:tab?`;

/**
 * Opens and closes the routed lifecycle event drawer
 * (`{basePath}/lifecycle-event/{id}/{tab}`). `canRoute` is false for an id that cannot be a
 * URL segment; callers keep an unrouted drawer for those.
 */
export const useLifecycleEventDrawerRoute = (basePath: string) => {
  const dialog = useRoutedDialog({
    path: OPEN_EVENT_PATH,
    basePath,
    end: false,
  });
  const { open: openDialog, close } = dialog;
  const open = useCallback(
    (eventId: string, tab?: AssetLifecycleManagementDrawerTabName) =>
      openDialog({ eventId, tab: toTabSlug(tab) }),
    [openDialog],
  );
  return {
    open,
    close,
    openEventId: dialog.isOpen ? dialog.params.eventId : undefined,
    canRoute: isRouteSafeSegment,
  };
};

/**
 * The lifecycle event drawer routed as `{basePath}/lifecycle-event/{id}/{tab}`, with the
 * event-date confirmation at `.../{tab}/event-date`. Resolves the id from `knownEvents`, else
 * looks it up by unique identifier. An id that does not exist shows a toast and returns to
 * `basePath`; a failed lookup closes quietly. Manual events have no Orders or Assets tab, so
 * those tab URLs fall back to the Event tab.
 */
export function RoutedLifecycleEventDrawer({
  basePath,
  knownEvents,
  hasAssetOrderDataManagePermission,
}: Readonly<{
  basePath: string;
  knownEvents?: readonly LifecycleManagementAllEvents[];
  hasAssetOrderDataManagePermission?: boolean;
}>) {
  const [isDirty, setIsDirty] = useState(false);
  const dialog = useRoutedDialog({
    path: LIFECYCLE_EVENT_PATH,
    basePath,
    end: false,
    isDirty,
  });
  // Save completes after an awaited request, possibly after the event-date entry closed,
  // so it closes through the latest callback rather than the one it captured.
  const closeRef = useRef(dialog.close);
  closeRef.current = dialog.close;
  const closeDrawer = useCallback(() => {
    closeRef.current();
  }, []);
  const { requestClose, discardPrompt } = useDiscardGuardedClose({
    open: dialog.isOpen,
    isDirty,
    onClose: closeDrawer,
  });

  const routedId = dialog.isOpen ? dialog.params.eventId : undefined;

  // Local copies so a save's optimistic update survives until the drawer reopens.
  const [editedEvents, setEditedEvents] = useState<
    Record<string, LifecycleManagementAllEvents | undefined>
  >({});
  const knownEvent = useMemo(
    () =>
      routedId === undefined
        ? undefined
        : knownEvents?.find((event) => event.id === routedId),
    [knownEvents, routedId],
  );
  const editedEvent =
    routedId === undefined ? undefined : editedEvents[routedId];
  const lookup = useGetLifecycleEventByIdentifier(
    routedId !== undefined && !knownEvent && !editedEvent
      ? routedId
      : undefined,
  );
  const resolved = editedEvent ?? knownEvent ?? lookup.data ?? undefined;

  const notFound =
    routedId !== undefined && !resolved && lookup.isSuccess && !lookup.data;
  const failed = routedId !== undefined && !resolved && lookup.isError;
  const unresolved = notFound || failed;

  useRouteNotFoundToast(
    notFound ? (routedId ?? null) : null,
    LIFECYCLE_EVENT_NOT_FOUND_MESSAGE,
    closeDrawer,
  );
  useEffect(() => {
    if (failed) {
      closeDrawer();
    }
  }, [failed, closeDrawer]);

  // Keep the last event rendered while the drawer animates closed.
  const [shown, setShown] = useState<{
    id: string;
    event: LifecycleManagementAllEvents;
  }>();
  useEffect(() => {
    if (routedId !== undefined && resolved) {
      setShown({ id: routedId, event: resolved });
    }
  }, [routedId, resolved]);
  const drawerId = routedId ?? shown?.id;
  const drawerEvent =
    resolved ?? (routedId === undefined ? shown?.event : undefined);

  const isManualEvent =
    drawerEvent?.source ===
    LIFECYCLE_MANAGEMENT_EVENT_DATA_SOURCE.ManuallyCreated;
  const visibleTabSlugs = useMemo(
    () =>
      TAB_SLUGS.filter(
        (tab) => !isManualEvent || !MANUAL_EVENT_HIDDEN_TABS.has(tab.name),
      ).map((tab) => tab.slug),
    [isManualEvent],
  );
  const eventBasePath =
    drawerId === undefined
      ? undefined
      : joinPath(
          basePath,
          LIFECYCLE_SEGMENTS.event,
          encodeRouteSegment(drawerId),
        );
  const { activeTab, setTab } = useRoutedTab({
    tabs: visibleTabSlugs,
    defaultTab: LIFECYCLE_EVENT_DEFAULT_TAB,
    basePath: eventBasePath,
    ready: !!resolved,
  });
  const handleTabChange = useCallback(
    (name: AssetLifecycleManagementDrawerTabName) => setTab(toTabSlug(name)),
    [setTab],
  );

  const eventDate = useRoutedDialog({
    path: LIFECYCLE_SEGMENTS.eventDate,
    basePath:
      routedId !== undefined && eventBasePath
        ? joinPath(eventBasePath, activeTab)
        : null,
    transient: true,
  });
  const { isOpen: isEventDateOpen, open: openEventDate } = eventDate;
  const eventDateCloseRef = useRef(eventDate.close);
  eventDateCloseRef.current = eventDate.close;
  const eventDateDialog = useMemo(
    () => ({
      isOpen: isEventDateOpen,
      open: () => openEventDate(),
      close: () => {
        eventDateCloseRef.current();
      },
    }),
    [isEventDateOpen, openEventDate],
  );

  const setSelectedEvent = useCallback(
    (action: SetStateAction<LifecycleManagementAllEvents | undefined>) => {
      if (drawerId === undefined) {
        return;
      }
      setEditedEvents((prev) => {
        const current =
          prev[drawerId] ?? (shown?.id === drawerId ? shown.event : undefined);
        const next = typeof action === "function" ? action(current) : action;
        return { ...prev, [drawerId]: next };
      });
    },
    [drawerId, shown],
  );

  useEffect(() => {
    if (!dialog.isOpen) {
      setIsDirty(false);
    }
  }, [dialog.isOpen]);

  if (drawerId === undefined) {
    return null;
  }

  if (routedId !== undefined && !resolved) {
    return unresolved ? null : <LoadingDrawer />;
  }

  return (
    <>
      <AssetLifecycleManagementDetailDrawer
        open={dialog.isOpen && !unresolved}
        handleClose={closeDrawer}
        requestClose={requestClose}
        onDirtyChange={setIsDirty}
        selectedLifecycleEventData={drawerEvent}
        setSelectedLifecycleEventData={setSelectedEvent}
        hasAssetOrderDataManagePermission={hasAssetOrderDataManagePermission}
        activeTab={toTabName(activeTab) ?? "Event"}
        onTabChange={handleTabChange}
        eventDateDialog={eventDateDialog}
      />
      {discardPrompt}
      {dialog.discardPrompt}
    </>
  );
}

function LoadingDrawer() {
  return (
    <Drawer anchor="right" open>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          width: (theme) => theme.spacing(60),
        }}
      >
        <CircularProgress aria-label="Loading lifecycle event" />
      </Box>
    </Drawer>
  );
}
